import type { ProgrammaticLandingPageConfig } from '../types'
import { KWP_SIZE_FACTS } from './kwpSizeFacts'

const FACTS = KWP_SIZE_FACTS[8]

export const KWP_8_PHOTOVOLTAIK_KOSTEN: ProgrammaticLandingPageConfig = {
  slug: '8-kwp-photovoltaik-kosten',
  lang: 'de',
  topicId: 'kwp-kosten-8',
  title: '8 kWp Photovoltaikanlage: Kosten & Richtwerte 2026',
  metaDescription:
    'Was kostet eine 8-kWp-Photovoltaikanlage? Dachfläche, Modulanzahl, Kostenaufschlüsselung und ' +
    'kostenloser Preisvergleich für 3-4-Personen-Haushalte.',
  keywords: [
    '8 kWp Photovoltaik Kosten',
    '8 kWp Solaranlage Preis',
    'Photovoltaikanlage 8 kWp',
    'Was kostet eine 8 kWp Anlage',
    '8 kWp PV-Anlage Haushalt',
  ],
  eyebrow: 'Anlagengröße 8 kWp',
  h1: '8 kWp Photovoltaikanlage: Kosten und Richtwerte',
  intro:
    'Eine 8-kWp-Anlage ist eine beliebte Zwischengröße für 3-4-Personen-Haushalte mit etwas ' +
    'höherem Verbrauch oder einem geplanten Elektroauto. Hier finden Sie Richtwerte zu Dachfläche ' +
    'und Modulanzahl, eine Kostenaufschlüsselung und direkt darunter einen Rechner mit dem für 8 kWp ' +
    'vorbelegten Durchschnittspreis aus echten Handwerker-Angeboten.',
  calculatorPrefill: { kind: 'kwp', kwp: 8 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Für wen eignet sich eine 8-kWp-Anlage?' },
    {
      type: 'paragraph',
      text:
        'Richtwert: 8 kWp passen typischerweise zu einem 3-4-Personen-Haushalt mit einem ' +
        'Jahresverbrauch von etwa 4.500-6.000 kWh, etwa wenn ein Elektroauto in Planung ist, aber ' +
        'noch keine Wärmepumpe eingerechnet werden muss. Kommt eine Wärmepumpe hinzu, ist meist ' +
        'eine der größeren Stufen (12 kWp aufwärts) die passendere Wahl.',
    },
    { type: 'heading', text: 'Dachfläche und Module: das braucht eine 8-kWp-Anlage' },
    {
      type: 'paragraph',
      text:
        `Richtwert: rund ${FACTS.roofAreaM2} m² Dachfläche und ca. ${FACTS.moduleCount} Module bei ` +
        `marktüblichen ~440-Wp-Modulen. Der Jahresertrag liegt bei Südausrichtung ohne Verschattung ` +
        `bei etwa ${FACTS.annualYieldKwh.toLocaleString('de-DE')} kWh. Eine genaue, dachspezifische ` +
        'Berechnung liefert der kostenlose PV-Rechner.',
    },
    { type: 'heading', text: 'Kostenbestandteile einer Photovoltaikanlage' },
    {
      type: 'list',
      items: [
        'Module: größter Einzelposten, meist rund ein Drittel der Gesamtkosten',
        'Wechselrichter: bei 8 kWp häufig noch ein einzelner String-Wechselrichter ausreichend',
        'Montage/Arbeitsleistung: Befestigung auf dem Dach, Verkabelung',
        'Gerüst: abhängig von Dachhöhe und -zugänglichkeit',
        'Elektroinstallation: Zählerschrank-Anpassung, Anschluss ans Hausnetz',
        'Anmeldung: Netzbetreiber und Marktstammdatenregister',
      ],
    },
    {
      type: 'paragraph',
      text:
        'Im Vergleich zu 5 kWp verteilen sich Anmeldung und Grundgerüst bei 8 kWp bereits auf mehr ' +
        'installierte Leistung - der Preis pro kWp fällt dadurch im Schnitt etwas günstiger aus.',
    },
    { type: 'heading', text: 'Amortisation und Eigenverbrauch' },
    {
      type: 'paragraph',
      text:
        'Ohne Batteriespeicher liegt der Eigenverbrauchsanteil bei einer 8-kWp-Anlage typischerweise ' +
        'bei rund 25-30 %, mit passend dimensioniertem Speicher und einem Elektroauto als ' +
        'zusätzlichem Verbraucher tagsüber oft deutlich höher. Details zu Einspeisevergütung und ' +
        'Förderungen im Förder-Überblick, eine genaue Berechnung für Ihr Dach liefert der PV-Rechner.',
    },
  ],
  faq: [
    {
      q: 'Für welche Haushalte eignet sich eine 8-kWp-Anlage?',
      a: 'Typischerweise für 3-4-Personen-Haushalte mit etwas höherem Verbrauch oder einem geplanten Elektroauto, aber ohne Wärmepumpe. Mit Wärmepumpe ist meist eine größere Stufe passender.',
    },
    {
      q: 'Brauche ich bei 8 kWp einen Dreiphasen-Wechselrichter?',
      a: 'Nicht zwingend - viele 8-kWp-Anlagen kommen noch mit einem einzelnen String-Wechselrichter aus. Der genaue Bedarf hängt vom Hausanschluss und der gewählten Technik ab, das klärt der Handwerksbetrieb im Angebot.',
    },
    {
      q: 'Wie viel Dachfläche brauche ich für 8 kWp?',
      a: `Richtwert: rund ${FACTS.roofAreaM2} m², abhängig vom gewählten Modultyp und der Dachausrichtung.`,
    },
  ],
  primaryCta: { label: 'Anlagengröße für mein Dach kostenlos berechnen', action: 'link', href: '/pv-rechner' },
  secondaryCta: { label: 'Direkt Anfrage erstellen', action: 'customer' },
  relatedLinks: [
    { label: '5 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/5-kwp-photovoltaik-kosten' },
    { label: '10 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/10-kwp-photovoltaik-kosten' },
    { label: '12 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/12-kwp-photovoltaik-kosten' },
    { label: 'Was kostet eine Solaranlage? Preise & Einflussfaktoren im Überblick', href: '/solaranlage-kosten' },
    { label: 'Photovoltaik-Förderung 2026: Deutschland & Österreich', href: '/blog/photovoltaik-foerderung-2026' },
    { label: 'PV-Kostenrechner: Durchschnittspreis für Ihre Anlage berechnen', href: '/pv-kostenrechner' },
  ],
}
